import { Box } from '@mui/material';
import React from 'react';

import { useInstallationGuide } from '../../hooks/useInstallationGuide';

import { ProgressBar } from './ProgressBar';
import {
  BoxContentsStep,
  ConfirmModelStep,
  ConfirmPowerStep,
  ConnectCoreStep,
  ConnectOverviewStep,
  ConnectPowerStep,
  ConnectRouterStep,
  ConnectionTestStep,
  GuideErrorStep,
  LoadingStep,
  PlacementStep,
  ReadinessCheckStep,
  ReadyTestStep,
  SessionMissingOrExpiredStep,
  WelcomeStep,
} from './steps';
import { WizardHeader } from './WizardHeader';

/**
 * Step component mapping by step ID
 */
const STEP_COMPONENTS: Record<string, React.FC> = {
  'loading': LoadingStep,
  'welcome': WelcomeStep,
  'session-missing': SessionMissingOrExpiredStep,
  'guide-error': GuideErrorStep,
  'confirm-model': ConfirmModelStep,
  'readiness-check': ReadinessCheckStep,
  'box-contents': BoxContentsStep,
  'placement': PlacementStep,
  'connect-overview': ConnectOverviewStep,
  'connect-router': ConnectRouterStep,
  'connect-core': ConnectCoreStep,
  'connect-power': ConnectPowerStep,
  'confirm-power': ConfirmPowerStep,
  'ready-test': ReadyTestStep,
  'connection-test': ConnectionTestStep,
};

/**
 * InstallationGuideWizard - Main container for the installation guide
 * 
 * Renders the header, progress bar, and current step content based on
 * the current step index from the context.
 */
export const InstallationGuideWizard: React.FC = () => {
  const {
    currentStepId,
    currentStepNumber,
    totalSteps,
    showBackButton,
    goToPreviousStep,
  } = useInstallationGuide();

  // Get the component for the current step
  const StepComponent = STEP_COMPONENTS[currentStepId] || GuideErrorStep;

  // Determine if we should show header/progress (not on loading screen)
  const showHeader = currentStepId !== 'loading';
  const showProgress = currentStepNumber !== null;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f8f9fa',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        // Mobile-first responsive design
        padding: { xs: 0, sm: 3, md: 4 },
      }}
    >
      {/* Mobile-optimized container */}
      <Box
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', sm: 420, md: 480 },
          margin: '0 auto',
          bgcolor: 'white',
          borderRadius: { xs: 0, sm: 2 },
          boxShadow: { xs: 'none', sm: '0 4px 6px rgba(0, 0, 0, 0.1)' },
          overflow: 'hidden',
          minHeight: { xs: '100vh', sm: 'auto' },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header with back button, logo, and step indicator */}
        {showHeader && (
          <WizardHeader
            stepNumber={currentStepNumber}
            totalSteps={totalSteps}
            showBack={showBackButton}
            onBack={goToPreviousStep}
          />
        )}

        {/* Progress bar */}
        {showProgress && (
          <ProgressBar
            current={currentStepNumber}
            total={totalSteps}
          />
        )}

        {/* Step content */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            p: { xs: 2, sm: 3 },
            bgcolor: '#fafafa',
          }}
        >
          <StepComponent />
        </Box>
      </Box>
    </Box>
  );
};
